import React, { useEffect } from "react";
import Navbar from "../Components/Navbar";
import { Button, message } from "antd";
import Footer from "../Components/Footer";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import HomeSupportVisual from "../Components/HomeSupportVisual";

const Home = () => {
  const navigate = useNavigate();
  const handleAppointmentClick = () => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/book-appointment");
    } else {
      navigate("/login");
    }
  };

  useEffect(() => {
    // Initialize AOS
    AOS.init({ duration: 1000, once: true });
    AOS.refresh();

    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");

    if (!token) {
      navigate("/");
      message.success("Session expired or not logged in. Please log in again!");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp < currentTime) {
        localStorage.removeItem("token");
        navigate("/");
        message.success(
          "Session expired or not logged in. Please log in again!",
        );
        return;
      }

      switch (role) {
        case "user":
          navigate("/userdash");
          break;
        case "manager":
          navigate("/managerdash");
          break;
        case "admin":
          navigate("/admindash");
          break;
        case "supervisor":
          navigate("/supervisordash");
          break;
        default:
          navigate("/");
          break;
      }
    } catch (error) {
      localStorage.removeItem("token");
      navigate("/");
      message.success("Session expired or not logged in. Please log in again!");
    }
  }, [navigate]);

  return (
    <div className="relative min-h-screen flex flex-col font-sans text-slate-800">
      <Navbar />

      <main className="relative flex-grow w-full px-12 lg:px-24 py-16 lg:py-24 mt-8  overflow-hidden bg-gradient-to-br from-blue-50/30 via-white to-indigo-50/30">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-24 relative z-10">
          <div
            data-aos="fade-up"
            className="relative w-full lg:w-5/12 flex flex-col items-center lg:items-start text-center lg:text-left"
          >
            <div className="absolute -inset-20 z-[-1] bg-[radial-gradient(#94a3b8_1.2px,transparent_1px)] [background-size:16px_16px] opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_70%)] pointer-events-none"></div>

            <svg
              className="absolute -top-10 -left-10 w-full h-full text-blue-400 z-[-2] pointer-events-none opacity-50"
              viewBox="0 0 400 400"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="8" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <linearGradient
                  id="lineGrad"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
                  <stop offset="50%" stopColor="#6366f1" stopOpacity="1" />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M 50,200 Q 150,50 250,250 T 450,150"
                fill="none"
                stroke="url(#lineGrad)"
                strokeWidth="3"
                filter="url(#glow)"
                className="animate-[dash_6s_linear_infinite]"
                strokeDasharray="400"
                strokeDashoffset="400"
              />
              <path
                d="M 20,150 Q 100,50 200,150 T 350,50"
                fill="none"
                stroke="url(#lineGrad)"
                strokeWidth="2"
                filter="url(#glow)"
                className="animate-[dash_8s_linear_infinite_reverse]"
                strokeDasharray="400"
                strokeDashoffset="400"
              />
              <style>{`
                @keyframes dash {
                  to { stroke-dashoffset: 0; }
                }
              `}</style>
            </svg>
            <div className="absolute bottom-[-20%] right-[-10%] w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-[100px] opacity-20 animate-pulse z-[-2] pointer-events-none"></div>

            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/60 text-blue-700 font-semibold text-xs mb-6 border border-blue-100 shadow-sm backdrop-blur-md relative z-10 uppercase tracking-widest">
              Digify Creation
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-5xl font-extrabold tracking-tight mb-5 text-slate-900 leading-[1.15]">
              Customer Help & <br className="hidden xl:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Support Services
              </span>
            </h1>

            <p className="text-base md:text-lg text-slate-600 mb-8 max-w-lg leading-relaxed">
              Designed to streamline your experience, our portal empowers you to
              effortlessly raise tickets for queries and book appointments with
              designated managers.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
              <Button
                onClick={handleAppointmentClick}
                type="primary"
                size="large"
                className="h-12 px-6 text-base font-semibold bg-blue-600 hover:bg-blue-700 border-none shadow-lg shadow-blue-600/20 rounded-xl w-full sm:w-auto flex items-center justify-center transition-all duration-300"
              >
                Book Appointment
              </Button>
              <Button
                size="large"
                className="h-12 px-6 text-base font-semibold text-slate-700 hover:text-blue-600 border-slate-200 hover:border-blue-200 rounded-xl w-full sm:w-auto flex items-center justify-center transition-all duration-300 bg-white shadow-sm"
                onClick={() =>
                  document
                    .getElementById("guides")
                    .scrollIntoView({ behavior: "smooth" })
                }
              >
                Watch Guides
              </Button>
            </div>
          </div>

          {/* Right Content / Images */}
          <div
            data-aos="fade-left"
            data-aos-delay="100"
            className="hidden lg:flex w-full lg:w-7/12 relative justify-center lg:justify-end xl:justify-center mt-12 lg:mt-0"
          >
            <HomeSupportVisual />
          </div>
        </div>
      </main>

      {/* Video Guides Section */}
      <section
        id="guides"
        className="w-full bg-white/70 backdrop-blur-xl border-t border-slate-100/50 py-24 mt-12 relative z-10"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How to use this portal
            </h2>
            <div className="w-20 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto rounded-full mb-6 relative">
              <div className="absolute inset-0 bg-blue-400 blur-sm opacity-50"></div>
            </div>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Follow these simple video tutorials to understand how to raise
              tickets, book appointments, and navigate the platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {/* Card 1 */}
            {/* <div
              data-aos="fade-up"
              data-aos-delay="0"
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-xl shadow-slate-200/40 border border-slate-100 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-300"
            >
              <div className="aspect-video w-full rounded-xl overflow-hidden bg-slate-100 mb-5 relative">
                <iframe
                  sandbox="allow-same-origin allow-scripts"
                  src="https://drive.google.com/file/d/1vGs4zzudQQqVtwInFR_rvA1jBPRjd4ZA/preview"
                  width="100%"
                  height="100%"
                  allow="autoplay"
                  className="w-full h-full border-0 absolute inset-0"
                  title="Raising a Ticket"
                ></iframe>
              </div>
              <h3 className="text-xl font-bold text-slate-800 px-2">
                Raising a Ticket
              </h3>
              <p className="text-slate-500 px-2 mt-2 text-sm leading-relaxed mb-2">
                Learn how to easily submit support queries and get rapid
                assistance.
              </p>
            </div> */}

            {/* Card 2 */}
            <div
              data-aos="fade-up"
              data-aos-delay="100"
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-xl shadow-slate-200/40 border border-slate-100 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-300"
            >
              <div className="aspect-video w-full rounded-xl overflow-hidden bg-slate-100 mb-5 relative">
                <iframe
                  sandbox="allow-same-origin allow-scripts"
                  src="https://drive.google.com/file/d/1gN4kGZQOTaL4NDkN6hjzwDzbIqiHn4gF/preview"
                  width="100%"
                  height="100%"
                  allow="autoplay"
                  className="w-full h-full border-0 absolute inset-0"
                  title="Booking an Appointment"
                ></iframe>
              </div>
              <h3 className="text-xl font-bold text-slate-800 px-2">
                Booking an Appointment
              </h3>
              <p className="text-slate-500 px-2 mt-2 text-sm leading-relaxed mb-2">
                Schedule meetings directly with designated managers at your
                convenience.
              </p>
            </div>

            {/* Card 3 */}
            <div
              data-aos="fade-up"
              data-aos-delay="200"
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-xl shadow-slate-200/40 border border-slate-100 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-300"
            >
              <div className="aspect-video w-full rounded-xl overflow-hidden bg-slate-100 mb-5 relative">
                <iframe
                  sandbox="allow-same-origin allow-scripts"
                  src="https://drive.google.com/file/d/10mT5cnmy2E-7IAYjjc5zBqYkn0uuvzcD/preview"
                  width="100%"
                  height="100%"
                  allow="autoplay"
                  className="w-full h-full border-0 absolute inset-0"
                  title="Portal Overview"
                ></iframe>
              </div>
              <h3 className="text-xl font-bold text-slate-800 px-2">
                Portal Overview
              </h3>
              <p className="text-slate-500 px-2 mt-2 text-sm leading-relaxed mb-2">
                Get a complete walkthrough of all the features available in the
                portal.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
